/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, {
  type AxiosInstance,
  AxiosInterceptorManager,
  AxiosResponse,
  AxiosError,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
  AxiosRequestHeaders,
} from "axios";
import { captureException } from "utils/sentry";

export type CustomResponseFormat<T = any> = {
  code: number;
  debugMessage?: string;
  message?: string;
  status?: number;
  error?: string;
  data?: T;
  result?: T;
};

interface CustomInstance extends AxiosInstance {
  interceptors: {
    request: AxiosInterceptorManager<InternalAxiosRequestConfig>;
    response: AxiosInterceptorManager<AxiosResponse<CustomResponseFormat>>;
  };
  getUri(config?: AxiosRequestConfig): string;
  request<T>(config: AxiosRequestConfig): Promise<T>;
  get<T>(url: string, config?: AxiosRequestConfig): Promise<T>;
  delete<T>(url: string, config?: AxiosRequestConfig): Promise<T>;
  head<T>(url: string, config?: AxiosRequestConfig): Promise<T>;
  options<T>(url: string, config?: AxiosRequestConfig): Promise<T>;
  post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>;
  put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>;
  patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>;
}
// Extend AxiosRequestConfig to include _retry
interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

const onRequest = (config: CustomAxiosRequestConfig) => {
  console.info(
    "[",
    config.method?.toUpperCase(),
    config.url,
    "]",
    "\nparams: ",
    config.params
  );
  return config;
};
const HOST_API = process.env.REACT_APP_API_SERVER_HOST;
const API_KEY = process.env.REACT_APP_API_KEY;

const axiosInstanceV2: CustomInstance = axios.create({
  withCredentials: true,
  baseURL: HOST_API,
  headers: {
    "X-Api-Key": API_KEY,
    "content-type": "application/json",
    // "X-Api-Key": API_KEY,
    // "X-Auth-Token": "",
    // "X-App-Id": "w:123",
  },
});
const onFulfilled = (response: AxiosResponse) => {
  return response.data;
};
const onRejected = async (error: AxiosError) => {
  const originalRequest = error.config as CustomAxiosRequestConfig & {
    _retryCount?: number;
  };

  // 네트워크 에러 처리
  if (!error.response && error.message === "Network Error") {
    console.error("Network error:", error.message);

    // 첫 Network Error는 warning으로 보고
    captureException(error, {
      tags: { action: "axios_network_error" },
      level: "warning",
    });

    // Network Error 시 자동 재시도 (최대 2회)
    const retryCount = originalRequest._retryCount || 0;
    if (retryCount < 2) {
      console.log(`Retrying request (attempt ${retryCount + 1}/2)...`);
      originalRequest._retryCount = retryCount + 1;

      // 재시도 전 짧은 대기
      await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));

      try {
        return await axiosInstanceV2(originalRequest);
      } catch (retryError) {
        // 재시도도 실패한 경우
        if (originalRequest._retryCount >= 2) {
          console.error("All retry attempts failed. Reloading page...");

          // Sentry에 재시도 실패 보고
          captureException(retryError, {
            tags: { action: "axios_network_error_retry_failed" },
            extra: { retryCount: originalRequest._retryCount },
          });

          // 페이지 새로고침
          window.location.href = window.location.href;
        }
        throw retryError;
      }
    }
  }

  if (error.response && error.response.status === 401) {
    if (!originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const response = await axios.get(`${HOST_API}/user/v2/token/refresh`, {
          withCredentials: true,
        });
        const newAccessToken = response.data.result.accessToken;
        if (newAccessToken) {
          console.log("newAccessToken", newAccessToken);
          axiosInstanceV2.defaults.headers[
            "Authorization"
          ] = `Bearer ${newAccessToken}`;
          if (originalRequest.headers) {
            originalRequest.headers[
              "Authorization"
            ] = `Bearer ${newAccessToken}`;
          } else {
            originalRequest.headers = {
              Authorization: `Bearer ${newAccessToken}`,
            } as AxiosRequestHeaders;
          }
        }

        return axiosInstanceV2(originalRequest);
      } catch (err: any) {
        if (err.response.status === 401) {
          console.log("refresh token 없음");
          // return null;
        }

        return Promise.reject(err);
      }
    }
  }
  return Promise.reject(error);
};

axiosInstanceV2.interceptors.request.use(onRequest, (error: AxiosError) => {
  return Promise.reject(error);
});
axiosInstanceV2.interceptors.response.use(onFulfilled, onRejected);

export const setBearerToken = (accessToken: string) => {
  if (axiosInstanceV2.defaults?.headers) {
    // @ts-ignore
    axiosInstanceV2.defaults.headers["Authorization"] = `Bearer ${accessToken}`;
  }
};

export { axiosInstanceV2 };
